import { useCallback, useEffect } from 'react';
import { useClickStore } from '../store/clickStore';
import { useSectionStore } from '../store/sectionStore';
import { useShallow } from 'zustand/react/shallow';
import { getLevelLabels } from '../config/siteConfig';
import { useStringLayerStore } from '../store/stringLayerStore';

const CURSOR_BY_TOOL = {
  pan: 'pointer',
  info: 'help',
  distance: 'crosshair',
  zoom: 'zoom-in',
  color: 'move',
  hide: 'not-allowed',
};

function isTypingTarget(target) {
  if (!target) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Scene tool shortcuts — always active while the scene is shown.
 */
export function useSceneToolShortcuts({
  enabled = true,
  onHelp,
  onUndoHide,
  onScreenshot,
  onSearch,
  onToggleGrid,
} = {}) {
  const { clickType, setClickType, setZoomToExtent, setLabels } = useClickStore(
    useShallow((state) => ({
      clickType: state.clickType,
      setClickType: state.setClickType,
      setZoomToExtent: state.setZoomToExtent,
      setLabels: state.setLabels,
    }))
  );

  const activateTool = useCallback(
    (id) => {
      const current = useClickStore.getState().clickType;
      if (id !== 'pan' && current === id) {
        setClickType('pan');
        return;
      }
      setClickType(id);
    },
    [setClickType]
  );

  const placeMode = useSectionStore((s) => s.placeMode);
  const digitizing = useStringLayerStore((s) => s.digitizing);

  useEffect(() => {
    if (!enabled) {
      document.body.style.cursor = 'default';
      return undefined;
    }
    document.body.style.cursor =
      digitizing || (placeMode && placeMode !== 'idle') ? 'crosshair' : CURSOR_BY_TOOL[clickType] || 'default';
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [clickType, enabled, placeMode, digitizing]);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleKeyPress = (event) => {
      if (isTypingTarget(event.target)) return;

      const key = event.key.toLowerCase();
      if (key === 'escape') {
        if (useStringLayerStore.getState().digitizing) {
          useStringLayerStore.getState().cancelDigitize();
          return;
        }
        useSectionStore.getState().exitSection();
        setClickType('pan');
        window.dispatchEvent(new CustomEvent('mdv-escape'));
        return;
      }
      if (key === '?' || key === 'f1') {
        event.preventDefault();
        onHelp?.();
        return;
      }
      if (event.key === '/') {
        event.preventDefault();
        onSearch?.();
        return;
      }

      if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;

      switch (key) {
        case 'e':
          event.preventDefault();
          setZoomToExtent(true);
          break;
        case 'p':
          event.preventDefault();
          activateTool('pan');
          break;
        case 'i':
          event.preventDefault();
          activateTool('info');
          break;
        case 'd':
          event.preventDefault();
          activateTool('distance');
          break;
        case 'z':
          event.preventDefault();
          activateTool('zoom');
          break;
        case 'c':
          event.preventDefault();
          activateTool('color');
          break;
        case 'h':
          event.preventDefault();
          activateTool('hide');
          break;
        case 'u':
          event.preventDefault();
          onUndoHide?.();
          break;
        case 's':
          event.preventDefault();
          onScreenshot?.();
          break;
        case 'l':
          event.preventDefault();
          window.dispatchEvent(new CustomEvent('toggleUnifiedPanel'));
          break;
        case 'b':
          if (!getLevelLabels()?.levels?.length) break;
          event.preventDefault();
          setLabels();
          break;
        case 'g':
          event.preventDefault();
          onToggleGrid?.();
          break;
        case 'n':
          event.preventDefault();
          useClickStore.getState().togglePlanLocked();
          break;
        case 'x':
          event.preventDefault();
          {
            const section = useSectionStore.getState();
            const wasActive = section.panelOpen || section.enabled;
            section.togglePanel();
            if (wasActive) setClickType('pan');
          }
          break;
        case '[':
          event.preventDefault();
          if (useSectionStore.getState().enabled) useSectionStore.getState().step(-1);
          break;
        case ']':
          event.preventDefault();
          if (useSectionStore.getState().enabled) useSectionStore.getState().step(1);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [enabled, setZoomToExtent, activateTool, setClickType, setLabels, onHelp, onUndoHide, onScreenshot, onSearch, onToggleGrid]);
}
